import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Eye, EyeOff, ArrowLeft, Mail, Lock } from "lucide-react";import { formatApiError } from "../lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password);
      if (data.role === "admin") navigate("/admin", { replace: true });
      else if (data.role === "driver") navigate("/driver", { replace: true });
      else if (data.role === "partner") navigate("/partner", { replace: true });
      else navigate("/passenger", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        const msg = formatApiError(detail);
        setError(msg === "Invalid credentials" ? "Email ou mot de passe incorrect" : msg);
      } else {
        setError("Erreur de connexion. Vérifiez votre connexion internet.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-[#F5F7FA]"
      style={{ fontFamily: "Outfit, Inter, sans-serif" }}
      data-testid="login-page"
    >
      <div className="max-w-md mx-auto min-h-screen flex flex-col px-6 py-6">
        {/* Back button */}
        <Link
          to="/"
          className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#0A2540] hover:bg-[#0A2540] hover:text-white transition"
          data-testid="login-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>

        {/* Brand */}
        <div className="mt-8 mb-8 flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-[#0A2540] flex items-center justify-center overflow-hidden">
            <img src="/trigo-logo.png" alt="TrigoTour" className="w-12 h-12 object-contain" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#94A3B8]">
              Bienvenue
            </p>
            <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
              Connectez-vous
            </h1>
          </div>
        </div>

        <p className="text-sm text-[#64748B] mb-8 -mt-4">
          Retrouvez vos trajets, locations et courses Express.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 flex-1">
          <div>
            <label className="block text-[11px] uppercase tracking-wide font-bold text-[#64748B] mb-2 ml-3">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="w-full h-14 pl-11 pr-4 rounded-2xl bg-white border border-[#E2E8F0] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none text-[#0A2540] text-sm"
                data-testid="login-email"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wide font-bold text-[#64748B] mb-2 ml-3">
              Mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                required
                className="w-full h-14 pl-11 pr-11 rounded-2xl bg-white border border-[#E2E8F0] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none text-[#0A2540] text-sm"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0A2540]"
                aria-label={showPw ? "Cacher" : "Afficher"}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="text-right pt-1">
            <Link
              to="/forgot-password"
              className="text-xs text-[#64748B] hover:text-[#0A2540] hover:underline transition"
              data-testid="forgot-password-link"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          {error && (
            <div
              className="bg-[#EF4444]/10 border border-[#EF4444]/20 text-[#EF4444] text-xs rounded-xl px-4 py-3"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-bold text-base shadow-lg shadow-[#FACC15]/20 transition inline-flex items-center justify-center disabled:opacity-70"
            data-testid="login-submit-btn"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </form>

        <p className="text-center text-sm text-[#64748B] mt-8 pb-4">
          Pas encore de compte ?{" "}
          <Link to="/register" className="text-[#0A2540] font-bold hover:underline" data-testid="go-to-register">
            S&apos;inscrire
          </Link>
        </p>

        <Link
          to="/vehicles"
          className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full border border-[#0A2540]/15 text-[#0A2540] font-semibold text-sm hover:bg-[#0A2540] hover:text-white transition mt-1 mb-3"
          data-testid="explore-without-account"
        >
          <Eye className="w-4 h-4" /> Explorer sans compte
        </Link>

        <p className="text-center text-[11px] text-[#64748B] pb-6">
          <Link
            to="/terms"
            className="underline hover:text-[#0A2540] font-medium"
            data-testid="consult-terms-link"
          >
            Consulter nos termes et conditions
          </Link>
        </p>
      </div>
    </div>
  );
}
